import { Box, useTheme, Button } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import {
  AccountBalanceWalletOutlined,
  CardGiftcardOutlined,
  LockOutlined,
  Groups,
} from "@mui/icons-material";
import { useGetWalletListQuery } from "../../state/api";
import DataGridCustomToolbar from "../../components/DataGridCustomToolbar";
import StatBox from "../../components/StatBox";

const Wallet = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const { data, isLoading } = useGetWalletListQuery();
  const [rowData, setRowData] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (data) {
      setRowData(data);
    }
  }, [data]);

  const totals = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    return rows.reduce(
      (acc, row) => ({
        wallets: acc.wallets + 1,
        credit: acc.credit + (row.creditPoints || 0),
        reward: acc.reward + (row.rewardPoints || 0),
        locked:
          acc.locked + (row.lockedCreditPoints || 0) + (row.lockedRewardPoints || 0),
      }),
      { wallets: 0, credit: 0, reward: 0, locked: 0 }
    );
  }, [data]);

  const handleWalletHistory = async (userId) => {
    try {
      navigate(`/wallet/history/${userId}`);
      console.log("user", userId);
    } catch (error) {
      console.log("Error fetching wallet history", error);
      toast.error("Error fetching wallet history");
    }
  };

  const columns = [
    {
      field: "fullname",
      headerName: "Full name",
      flex: 0.5,
    },
    {
      field: "creditPoints",
      headerName: "Credit Points",
      flex: 0.5,
    },
    {
      field: "rewardPoints",
      headerName: "Reward Points",
      flex: 0.5,
    },
    {
      field: "lockedCreditPoints",
      headerName: "lockedCreditPoints",
      flex: 0.5,
    },
    {
      field: "lockedRewardPoints",
      headerName: "lockedRewardPoints",
      flex: 0.5,
    },
    {
      field: "createdAt",
      headerName: "createdAt",
      flex: 0.5,
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "yyyy-MM-dd");
      },
    },
    {
      field: "updatedAt",
      headerName: "updatedAt",
      flex: 0.5,
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "yyyy-MM-dd");
      },
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.6,
      renderCell: (params) => (
        <div>
          <Button
            color="info"
            variant="contained"
            onClick={() => handleWalletHistory(params.row.userId)}
          >
            <b> view statement</b>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <ToastContainer />
      <Header title="WALLET" subtitle="Platform-wide credit & reward balances" />

      <Box
        mt="20px"
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }}
        gap="20px"
      >
        <StatBox
          title="Total Wallets"
          value={totals.wallets}
          caption="Registered wallets"
          loading={isLoading}
          accent="#24baef"
          icon={<Groups />}
        />
        <StatBox
          title="Credit Points"
          value={totals.credit}
          caption="In circulation"
          loading={isLoading}
          accent="#00c9a7"
          icon={<AccountBalanceWalletOutlined />}
        />
        <StatBox
          title="Reward Points"
          value={totals.reward}
          caption="In circulation"
          loading={isLoading}
          accent="#a370d1"
          icon={<CardGiftcardOutlined />}
        />
        <StatBox
          title="Locked Funds"
          value={totals.locked}
          caption="Credit + reward on hold"
          loading={isLoading}
          accent="#de4437"
          icon={<LockOutlined />}
        />
      </Box>

      <Box
        mt="20px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.success,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={rowData}
          columns={columns}
          autoHeight
          disableColumnMenu
          disableColumnSelector
          hideFooter
          rowCount={(rowData && rowData.total) || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{
            Toolbar: DataGridCustomToolbar,
          }}
          componentsProps={{
            toolbar: {
              searchInput,
              setSearchInput,
              setSearch,
              columns,
              rowData,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Wallet;
